import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, FileText, FileSpreadsheet, RefreshCw } from "lucide-react";
import { api, API, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const tone = { P: "text-emerald-700", H: "text-amber-700", A: "text-red-700", L: "text-blue-700", W: "text-red-700", O: "text-slate-400", "-": "text-slate-300" };
const LEGEND = [["P", "Present"], ["H", "Half Day"], ["A", "Absent"], ["L", "Paid Leave"], ["W", "LWP (unpaid)"], ["O", "Holiday"], ["-", "Not marked"]];

export default function AttendanceRegister() {
  const [month, setMonth] = useState(thisMonth());
  const [dept, setDept] = useState("all");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance/register?month=${month}&department=${dept === "all" ? "" : encodeURIComponent(dept)}`);
      setD(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, dept]);

  const download = (kind) => {
    const token = localStorage.getItem("token");
    const q = `token=${token}&month=${month}&department=${dept === "all" ? "" : encodeURIComponent(dept)}`;
    window.open(kind === "pdf" ? `${API}/pdf/attendance-register?${q}` : `${API}/attendance/register/xlsx?${q}`, "_blank");
  };

  const days = Array.from({ length: d?.days_in_month || 0 }, (_, i) => i + 1);
  const t = d?.totals || {};

  return (
    <div data-testid="attendance-register">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <CalendarRange className="w-6 h-6 text-[#0B132B]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Attendance Register</h1>
            <p className="text-sm text-slate-500 mt-0.5">{d?.month_label || month} · {dept === "all" ? "All departments" : dept} · {d?.rows?.length || 0} employee(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input data-testid="register-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-white" />
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="register-department" className="w-44 bg-white h-9"><SelectValue placeholder="All departments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {(d?.departments || []).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="register-refresh" size="icon" variant="outline" className="rounded-sm h-9 w-9" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
          <Button data-testid="register-pdf-btn" variant="outline" className="rounded-sm gap-1.5" onClick={() => download("pdf")}><FileText className="w-4 h-4" /> <span className="hidden sm:inline">PDF</span></Button>
          <Button data-testid="register-xlsx-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => download("xlsx")}><FileSpreadsheet className="w-4 h-4" /> <span className="hidden sm:inline">Excel</span></Button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap text-sm">
        {LEGEND.map(([k, v]) => (
          <div key={k} className="px-2.5 py-1 rounded-md bg-white border border-slate-200">
            <span className={`font-bold ${tone[k]}`}>{k}</span> <span className="text-slate-500">{v}</span>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50">Employee</th>
            {days.map((n) => <th key={n} className="px-1 py-2.5 text-center text-[10px] font-semibold text-slate-500 w-6">{n}</th>)}
            {["P", "H", "A", "L", "W", "O", "NM", "Pay"].map((h) => (
              <th key={h} className="px-2 py-2.5 text-right text-[10px] font-semibold uppercase text-slate-500">{h}</th>))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={days.length + 9} className="px-4 py-16 text-center text-slate-400">Loading register...</td></tr>
              : !d?.rows?.length ? <tr><td colSpan={days.length + 9} className="px-4 py-16 text-center text-slate-400" data-testid="register-empty">No employees found</td></tr>
                : d.rows.map((r) => (
                  <tr key={r.employee_id} data-testid={`register-row-${r.employee_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800 sticky left-0 bg-white">
                      {r.employee_name}<div className="text-[10px] text-slate-400">{r.employee_code || "-"} · {r.department}</div>
                    </td>
                    {r.days.map((c, i) => <td key={i} className={`px-1 py-2 text-center font-bold ${tone[c] || "text-slate-300"}`}>{c || ""}</td>)}
                    <td className="px-2 py-2 text-right tabular-nums text-emerald-700">{r.present_days}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-amber-700">{r.half_days}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-red-700">{r.absent_days}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-blue-700">{r.leave_days}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-red-700">{r.lwp_days}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-500">{r.holiday_days}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-500">{r.unmarked_days}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold text-slate-900">{r.payable_days}</td>
                  </tr>))}
            {!loading && d?.rows?.length > 0 && (
              <tr className="bg-slate-50 font-semibold" data-testid="register-totals">
                <td className="px-3 py-2 sticky left-0 bg-slate-50">TOTAL</td>
                {days.map((n) => <td key={n} />)}
                <td className="px-2 py-2 text-right tabular-nums">{t.present_days}</td>
                <td className="px-2 py-2 text-right tabular-nums">{t.half_days}</td>
                <td className="px-2 py-2 text-right tabular-nums">{t.absent_days}</td>
                <td className="px-2 py-2 text-right tabular-nums">{t.leave_days}</td>
                <td className="px-2 py-2 text-right tabular-nums">{t.lwp_days}</td>
                <td className="px-2 py-2 text-right tabular-nums">{t.holiday_days}</td>
                <td className="px-2 py-2 text-right tabular-nums">{t.unmarked_days}</td>
                <td className="px-2 py-2 text-right tabular-nums">{t.payable_days}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
